import * as React from 'react';
import { useMemo, useState } from 'react';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Alert, DatePicker, InputNumber, message, Modal, Select, Timeline } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import {
	getDefaultFileListForImagesFromString,
	getProtectedImageUriForBuyer,
	getProtectedImageUriForSupplier,
} from '../../utils/FileAccessUtils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	compareStrings,
	getDisabledDate,
	getDisabledDateTime,
	getImageCountErrorRules,
	getObjectValues,
	getServiceCallConfigValueBasedOnSupplierOrInternalTeam,
	getWorkOrderTimeZoneId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { imageUploadValidation, normFile, imageWildcardUploadAccept } from '../../utils/ImageUtils';
import {
	clampCheckOutToCheckIn,
	formatDateForLocationTimeZone,
	getTimeDuration,
	wallTimeToUtcIso,
} from '../../utils/DataFormatterUtils';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import {
	COMPLETED_CHECKOUT_VALUES,
	CONTRACTOR_CHECK_OUT_OPTIONS,
	INTERNAL_TECH_CHECK_OUT_OPTIONS,
	PARTS_ON_ORDER_CHECK_OUT_OPTION,
	PARTS_REQUESTED_CHECK_OUT_OPTION,
} from '../../constants/ServiceCallCheckOutOptions';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import {
	serviceCallsRestCrudThunksForBuyer,
	serviceCallsRestCrudThunksForSupplier,
} from '../../thunks/service_calls_thunks';
import { resolutionTypesRestCrudThunksForSupplier } from '../../thunks/resolution_types_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import OWUpload from '../OWUpload';
import TextAreaWithAudio from '../audio_note_recorder/TextAreaWithAudio';
import DateTimeHover from '../date_time_component/DateTime';
import { DateTimeWithTimeZone } from '../date_time_with_time_zone/DateTimeWithTimeZone';

const FormItem = Form.Item;

const HEADER_FORMAT = 'h:mm a z on MMM D';

const fileListToPhotoStrings = (fileList) =>
	(fileList || [])
		.filter((file) => nullSafeGet('response.data.fileId', file))
		.map((file) => {
			const fileName = nullSafeGetOrElse('response.data.fileName', file, '');
			const fileId = nullSafeGetOrElse('response.data.fileId', file, '');
			return `${fileId}/${fileName}`;
		});

const SectionHeader = ({ label, expandable, expanded, onToggle }) => (
	<div
		role={expandable ? 'button' : undefined}
		tabIndex={expandable ? 0 : undefined}
		onClick={expandable ? onToggle : undefined}
		onKeyDown={
			expandable
				? (e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onToggle();
						}
				  }
				: undefined
		}
		className="flex items-center justify-between"
		style={{ cursor: expandable ? 'pointer' : 'default', userSelect: 'none' }}
	>
		<div style={{ fontSize: 16 }}>{label}</div>
		{expandable ? (
			<LegacyIcon type={expanded ? 'down' : 'right'} style={{ color: 'rgba(0,0,0,0.45)' }} />
		) : null}
	</div>
);

interface ReviseServiceCallModalProps extends FormComponentProps {
	visible: boolean;
	onCancel: () => void;
	onSuccess?: () => void;
	workOrder: any;
	serviceCall?: any;
	initialFocus?: 'checkIn' | 'checkOut';
	userType: string;

	currentUser: any;
	companyConfig: any;
	updating: boolean;
	resolutionTypes: any;
	fetchResolutionTypes: any;
	fetchMultipleResolutionTypes: any;
	updateServiceCall: (entity: any) => Promise<any>;
}

const ReviseServiceCallModalInner: React.FC<ReviseServiceCallModalProps> = (props) => {
	const {
		visible,
		onCancel,
		onSuccess,
		workOrder,
		form,
		currentUser,
		companyConfig,
		updating,
		resolutionTypes,
		fetchResolutionTypes,
		fetchMultipleResolutionTypes,
		updateServiceCall,
	} = props;

	const { getFieldDecorator, getFieldValue } = form;
	const serviceCall = props.serviceCall || nullSafeGetOrElse('lastServiceCall', workOrder, {});

	const userType = props.userType;
	const isSupplierContext = compareStrings(userType, ROLE_TYPES.SUPPLIER);
	const getUri = isSupplierContext ? getProtectedImageUriForSupplier : getProtectedImageUriForBuyer;
	const uploadAction = `${getBackendUri()}/api/v1/${userType}/file/upload`;
	const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };

	const initialCheckInPhotos = useMemo(
		() =>
			getDefaultFileListForImagesFromString(
				serviceCall.checkInImages,
				userType,
				serviceCall.convertedImages
			),
		[serviceCall.checkInImages, serviceCall.convertedImages, userType]
	);
	const initialCheckOutPhotos = useMemo(
		() =>
			getDefaultFileListForImagesFromString(
				serviceCall.checkOutImages,
				userType,
				serviceCall.convertedImages
			),
		[serviceCall.checkOutImages, serviceCall.convertedImages, userType]
	);

	const [checkInPhotos, setCheckInPhotos] = useState<any[]>(initialCheckInPhotos);
	const [checkOutPhotos, setCheckOutPhotos] = useState<any[]>(initialCheckOutPhotos);
	const [checkInPreview, setCheckInPreview] = useState<{ visible: boolean; src: string | null }>({
		visible: false,
		src: null,
	});
	const [checkOutPreview, setCheckOutPreview] = useState<{ visible: boolean; src: string | null }>({
		visible: false,
		src: null,
	});
	const [audioBusy, setAudioBusy] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const initialExpanded =
		props.initialFocus === 'checkIn'
			? { checkIn: true, checkOut: false }
			: props.initialFocus === 'checkOut'
			? { checkIn: false, checkOut: true }
			: { checkIn: true, checkOut: true };
	const [expanded, setExpanded] = useState(initialExpanded);

	const checkInNotesMandatory = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
		workOrder,
		currentUser,
		'checkInNotesMandatory',
		false
	);
	const numCheckInImagesRequired = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
		workOrder,
		currentUser,
		'numImagesRequiredOnCheckIn',
		0
	);
	const checkOutNotesMandatory = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
		workOrder,
		currentUser,
		'checkOutNotesMandatory',
		false
	);
	const numCheckOutImagesRequired = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
		workOrder,
		currentUser,
		'numImagesRequiredOnCheckOut',
		0
	);

	const buildPreviewSetter =
		(setter: (state: { visible: boolean; src: string | null }) => void) => (obj: any) => {
			const fileId = nullSafeGet('response.data.fileId', obj);
			const fileName = nullSafeGet('response.data.fileName', obj);
			setter({
				visible: true,
				src: obj.url || (fileId ? getUri(fileId, fileName, 400, 400) : null),
			});
		};

	const buildImageChangeHandler = (setPhotos: (list: any[]) => void) => (info: any) => {
		if (info.file.status === 'done') {
			setPhotos(info.fileList);
		}
	};

	const buildImageRemoveHandler =
		(currentList: any[], setPhotos: (list: any[]) => void) => (file: any) => {
			setPhotos(currentList.filter((existing) => existing.uid !== file.uid));
		};

	const ADDITIONAL_CHECK_OUT_OPTIONS = nullSafeGetOrElse(
		'detail.config.partsOrderingConfig.needPartsRequisitionBeforeOrdering',
		companyConfig,
		false
	)
		? PARTS_REQUESTED_CHECK_OUT_OPTION
		: PARTS_ON_ORDER_CHECK_OUT_OPTION;
	const partsRequestedStatus = compareStrings(
		nullSafeGet('status', workOrder),
		WORK_ORDER_STATUSES.partsRequested
	);
	const baseCheckOutOptions = isSupplierContext
		? INTERNAL_TECH_CHECK_OUT_OPTIONS
		: CONTRACTOR_CHECK_OUT_OPTIONS;
	const checkOutOptions = [
		...(partsRequestedStatus ? [] : baseCheckOutOptions),
		...ADDITIONAL_CHECK_OUT_OPTIONS,
	];

	const locationTimeZone = nullSafeGet('location.timeZoneId', workOrder);
	const checkInTimeZone = getFieldValue('checkInTimeZone') || locationTimeZone;
	const checkOutTimeZone = getFieldValue('checkOutTimeZone') || locationTimeZone;
	const currentCheckOutStatus = getFieldValue('checkOutStatus');
	const allowCheckOutFieldEdits = !serviceCall.checkOutTime;

	// Constrain check-out against the check-in time currently in the form (which
	// the user may have just edited), not the original serviceCall value. The
	// edited field value is a wall-time moment; convert it to a UTC ISO instant
	// so it's comparable to the stored serviceCall.checkInTime fallback.
	const editedCheckInValue = getFieldValue('checkInTime');
	const currentCheckInTime = editedCheckInValue
		? wallTimeToUtcIso(editedCheckInValue, checkInTimeZone)
		: serviceCall.checkInTime;

	getFieldDecorator('checkInImages', {
		initialValue: fileListToPhotoStrings(initialCheckInPhotos),
	});
	getFieldDecorator('checkOutImages', {
		initialValue: fileListToPhotoStrings(initialCheckOutPhotos),
	});

	const resolutionsFetching = nullSafeGet('fetching', resolutionTypes);
	const hasResolutionTypes = resolutionsFetching
		? true
		: getObjectValues(nullSafeGetOrElse('records', resolutionTypes, {})).length > 0;

	const headerTimeZone = getWorkOrderTimeZoneId(workOrder);
	const scheduleHeader = serviceCall.serviceScheduledAt ? (
		<>
			Scheduled to arrive by{' '}
			<DateTimeHover
				timestamp={serviceCall.serviceScheduledAt}
				dateFormat={HEADER_FORMAT}
				showDate
				parseZone={false}
				timeZone={headerTimeZone}
			/>
			.
		</>
	) : (
		<span style={{ color: 'rgba(0,0,0,0.45)' }}>Not scheduled</span>
	);

	const checkInHeader = serviceCall.checkInTime ? (
		<>
			Started work at{' '}
			<DateTimeHover
				timestamp={serviceCall.checkInTime}
				dateFormat={HEADER_FORMAT}
				showDate
				timeZone={headerTimeZone}
			/>
			.
		</>
	) : (
		<span style={{ color: 'rgba(0,0,0,0.45)' }}>Not yet checked in</span>
	);

	const checkOutHeader = serviceCall.checkOutTime ? (
		<>
			Ended work at{' '}
			<DateTimeHover
				timestamp={serviceCall.checkOutTime}
				dateFormat={HEADER_FORMAT}
				showDate
				timeZone={headerTimeZone}
			/>
			.
		</>
	) : (
		<span style={{ color: 'rgba(0,0,0,0.45)' }}>Not yet checked out</span>
	);

	const checkInImageError = `You need to upload at least ${numCheckInImagesRequired} photo${
		numCheckInImagesRequired > 1 ? 's' : ''
	}`;
	const checkOutImageError = `You need to upload at least ${numCheckOutImagesRequired} photo${
		numCheckOutImagesRequired > 1 ? 's' : ''
	}`;

	const handleSubmit = () => {
		form.validateFieldsAndScroll((err, values) => {
			if (err) {
				setExpanded({ checkIn: true, checkOut: true });
				return;
			}

			const { text: checkInNotes, audioUrl: checkInAudio } = values.checkInNotes || {};
			const { text: checkOutNotes, audioUrl: checkOutAudio } = values.checkOutNotes || {};
			const checkInTimeZoneFinal = values.checkInTimeZone || locationTimeZone;
			const checkOutTimeZoneFinal = values.checkOutTimeZone || locationTimeZone;

			const checkInImages = fileListToPhotoStrings(checkInPhotos);
			const checkOutImages = fileListToPhotoStrings(checkOutPhotos);

			const resolvedCheckInTime = values.checkInTime
				? formatDateForLocationTimeZone(values.checkInTime, checkInTimeZoneFinal)
				: serviceCall.checkInTime;
			const resolvedCheckOutTime = clampCheckOutToCheckIn(
				resolvedCheckInTime,
				values.checkOutTime
					? formatDateForLocationTimeZone(values.checkOutTime, checkOutTimeZoneFinal)
					: serviceCall.checkOutTime
			);

			const payload = {
				...serviceCall,
				id: serviceCall.id,
				workOrderId: nullSafeGet('id', workOrder),

				checkInTime: resolvedCheckInTime,
				checkInTimeZone: checkInTimeZoneFinal,
				checkInImages,
				checkInNotes,
				checkInAudio,
				checkInByEmail: serviceCall.checkInByEmail || nullSafeGet('email', currentUser),

				checkOutStatus: values.checkOutStatus,
				estimatedCompletionDate: values.estimatedCompletionDate
					? values.estimatedCompletionDate.utc().format()
					: undefined,
				numberOfTechs: values.numberOfTechs,
				checkOutTime: resolvedCheckOutTime,
				checkOutTimeZone: checkOutTimeZoneFinal,
				checkOutImages,
				checkOutNotes,
				checkOutAudio,
				checkOutByEmail: serviceCall.checkOutByEmail || nullSafeGet('email', currentUser),
				resolutionTypeId: values.resolutionTypeId,
			};

			setSubmitting(true);
			updateServiceCall(payload)
				.then(() => {
					message.success('Service call revised.');
					if (onSuccess) onSuccess();
					onCancel();
				})
				.catch((errResp) => {
					message.error(typeof errResp === 'string' ? errResp : 'Failed to revise service call.');
				})
				.finally(() => setSubmitting(false));
		});
	};

	const checkInForm = (
		<div className="px-2 py-3">
			<DateTimeWithTimeZone
				form={form}
				location={nullSafeGet('location', workOrder)}
				displayName={'Check In Time'}
				timeZoneName={'checkInTimeZone'}
				dateName={'checkInTime'}
				initialValue={serviceCall.checkInTime}
				rules={[
					{
						type: 'object',
						required: false,
						message: 'Please select the time technician(s) started work.',
					},
					{
						message: 'Check in time must be before the current time.',
						validator: (_, value) => {
							if (!value) return Promise.resolve();
							const checkInUtc = moment(wallTimeToUtcIso(value, checkInTimeZone)).utc();
							const nowUtc = moment().utc().add(1, 'minute');
							if (checkInUtc.isAfter(nowUtc)) {
								return Promise.reject(new Error('Check in time must be before the current time.'));
							}
							return Promise.resolve();
						},
					},
				]}
				disabledDate={getDisabledDate(undefined, checkInTimeZone, moment())}
				disabledTime={getDisabledDateTime(undefined, checkInTimeZone, moment())}
			/>
			<FormItem label="Before Photos">
				{getFieldDecorator('checkInImagesUpload', {
					valuePropName: 'fileList',
					getValueFromEvent: normFile,
					initialValue: initialCheckInPhotos,
					rules: getImageCountErrorRules(numCheckInImagesRequired, checkInImageError),
				})(
					<OWUpload
						accept={imageWildcardUploadAccept}
						name="file"
						listType="picture-card"
						className="servicecall-image-uploader"
						multiple={true}
						headers={uploadHeaders}
						action={uploadAction}
						userType={userType}
						beforeUpload={imageUploadValidation}
						onChange={buildImageChangeHandler(setCheckInPhotos)}
						onPreview={buildPreviewSetter(setCheckInPreview)}
						onRemove={buildImageRemoveHandler(checkInPhotos, setCheckInPhotos)}
					>
						<div style={{ display: 'inline' }}>
							<LegacyIcon type="plus" />
							<div className="ant-upload-text">Upload</div>
						</div>
						<Modal
							visible={checkInPreview.visible}
							footer={null}
							onCancel={() => setCheckInPreview({ visible: false, src: null })}
						>
							<img alt="preview" style={{ width: '100%' }} src={checkInPreview.src || ''} />
						</Modal>
					</OWUpload>
				)}
			</FormItem>
			<FormItem label="Notes">
				{getFieldDecorator('checkInNotes', {
					initialValue: {
						text: serviceCall.checkInNotes,
						audioUrl: serviceCall.checkInAudio,
					},
					rules: [
						{
							validator: (_, val, callback) => {
								if (checkInNotesMandatory && !(val && val.text) && !(val && val.audioUrl)) {
									callback('Please add notes or a voice note');
								} else {
									callback();
								}
							},
						},
					],
				})(
					<TextAreaWithAudio
						style={{ maxWidth: 300 }}
						userType={userType}
						onBusyStateChange={(busy) => setAudioBusy(busy)}
					/>
				)}
			</FormItem>
		</div>
	);

	const checkOutForm = (
		<div className="px-2 py-3">
			<FormItem label="Check Out Status">
				{getFieldDecorator('checkOutStatus', {
					rules: [
						{
							required: true,
							message: 'Please select a status',
						},
					],
					initialValue:
						serviceCall.checkOutStatus ||
						(partsRequestedStatus
							? ADDITIONAL_CHECK_OUT_OPTIONS[0].value
							: baseCheckOutOptions[0].value),
				})(
					<Select style={{ maxWidth: 300 }} disabled={!allowCheckOutFieldEdits}>
						{checkOutOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.displayName}
							</Select.Option>
						))}
					</Select>
				)}
			</FormItem>
			{!COMPLETED_CHECKOUT_VALUES.includes(currentCheckOutStatus) ? (
				<FormItem label="Estimated completion date">
					{getFieldDecorator('estimatedCompletionDate', {
						initialValue: serviceCall.estimatedCompletionDate
							? moment(serviceCall.estimatedCompletionDate)
							: undefined,
					})(<DatePicker style={{ width: '100%' }} showTime={false} format={'MMMM D, YYYY'} />)}
				</FormItem>
			) : null}
			<FormItem label="Number of Techs">
				{getFieldDecorator('numberOfTechs', {
					rules: [
						{
							required: true,
							message: 'Please enter the number of techs involved',
						},
					],
					initialValue:
						serviceCall.numberOfTechs ||
						1 + nullSafeGetOrElse('additionalTechnicianEmails.length', serviceCall, 0),
				})(
					<InputNumber
						style={{ maxWidth: 100 }}
						placeholder="techs"
						disabled={!allowCheckOutFieldEdits}
					/>
				)}
			</FormItem>
			<DateTimeWithTimeZone
				form={form}
				location={nullSafeGet('location', workOrder)}
				displayName={'Check Out Time'}
				timeZoneName={'checkOutTimeZone'}
				dateName={'checkOutTime'}
				initialValue={serviceCall.checkOutTime}
				rules={[
					{
						type: 'object',
						required: allowCheckOutFieldEdits,
						message: 'Please select the time technician(s) finished work.',
					},
					{
						message: 'Check-out time must be after check-in time.',
						validator: (_, value) => {
							if (!value || !currentCheckInTime) return Promise.resolve();
							const checkOutUtc = moment(wallTimeToUtcIso(value, checkOutTimeZone)).utc();
							const checkInUtc = moment(currentCheckInTime).utc().subtract(1, 'minute');
							if (checkOutUtc.isBefore(checkInUtc)) {
								return Promise.reject(new Error('Check-out time must be after check-in time!'));
							}
							return Promise.resolve();
						},
					},
					{
						message: 'Check-out time must be before the current time.',
						validator: (_, value) => {
							if (!value) return Promise.resolve();
							const checkOutUtc = moment(wallTimeToUtcIso(value, checkOutTimeZone)).utc();
							const nowUtc = moment().utc().add(15, 'minute');
							if (checkOutUtc.isAfter(nowUtc)) {
								return Promise.reject(new Error('Check-out time must be before the current time!'));
							}
							return Promise.resolve();
						},
					},
				]}
				disabledDate={getDisabledDate(currentCheckInTime, checkOutTimeZone, moment())}
				disabledTime={getDisabledDateTime(
					currentCheckInTime ? moment(currentCheckInTime).subtract(1, 'minute') : undefined,
					checkOutTimeZone,
					moment().add(1, 'minute')
				)}
			/>
			<FormItem label="After Photos">
				{getFieldDecorator('checkOutImagesUpload', {
					valuePropName: 'fileList',
					getValueFromEvent: normFile,
					initialValue: initialCheckOutPhotos,
					rules: getImageCountErrorRules(numCheckOutImagesRequired, checkOutImageError),
				})(
					<OWUpload
						accept={imageWildcardUploadAccept}
						name="file"
						listType="picture-card"
						className="servicecall-image-uploader"
						multiple={true}
						headers={uploadHeaders}
						action={uploadAction}
						userType={userType}
						beforeUpload={imageUploadValidation}
						onChange={buildImageChangeHandler(setCheckOutPhotos)}
						onPreview={buildPreviewSetter(setCheckOutPreview)}
						onRemove={buildImageRemoveHandler(checkOutPhotos, setCheckOutPhotos)}
					>
						<div style={{ display: 'inline' }}>
							<LegacyIcon type="plus" />
							<div className="ant-upload-text">Upload</div>
						</div>
						<Modal
							visible={checkOutPreview.visible}
							footer={null}
							onCancel={() => setCheckOutPreview({ visible: false, src: null })}
						>
							<img alt="preview" style={{ width: '100%' }} src={checkOutPreview.src || ''} />
						</Modal>
					</OWUpload>
				)}
			</FormItem>
			<FormItem label="Notes">
				{getFieldDecorator('checkOutNotes', {
					initialValue: {
						text: serviceCall.checkOutNotes,
						audioUrl: serviceCall.checkOutAudio,
					},
					rules: [
						{
							validator: (_, val, callback) => {
								if (checkOutNotesMandatory && !(val && val.text) && !(val && val.audioUrl)) {
									callback('Please add notes or a voice note');
								} else {
									callback();
								}
							},
						},
					],
				})(
					<TextAreaWithAudio
						style={{ maxWidth: 300 }}
						userType={userType}
						onBusyStateChange={(busy) => setAudioBusy(busy)}
					/>
				)}
			</FormItem>
			{hasResolutionTypes ? (
				<FormItem label="Resolution Type">
					{getFieldDecorator('resolutionTypeId', {
						initialValue: serviceCall.resolutionTypeId,
					})(
						<OWAsyncSelect
							style={{ maxWidth: 800 }}
							stateSlice={resolutionTypes}
							targetCollectionName={'RESOLUTION_TYPES_AUTOCOMPLETE'}
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleResolutionTypes(ids, targetCollectionName);
							}}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) => {
								fetchResolutionTypes(
									{ name: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
							}}
							renderRecord={(resolutionType) => (
								<Select.Option key={resolutionType.id} value={resolutionType.id}>
									{resolutionType.resolutionCode ? `${resolutionType.resolutionCode} - ` : null}
									{resolutionType.resolutionType}
								</Select.Option>
							)}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
							allowClear
						/>
					)}
				</FormItem>
			) : null}
		</div>
	);

	return (
		<Modal
			visible={visible}
			width={640}
			title="Revise Service Call"
			okText="Save"
			onCancel={onCancel}
			onOk={handleSubmit}
			closable={false}
			confirmLoading={submitting || updating}
			okButtonProps={{ disabled: audioBusy }}
		>
			<Form layout="vertical" form={form}>
				{!serviceCall.id ? (
					<FormItem>
						<Alert message="No service call to revise on this work order." type="warning" />
					</FormItem>
				) : null}
				<Timeline>
					<Timeline.Item>
						<SectionHeader
							label={scheduleHeader}
							expandable={false}
							expanded={false}
							onToggle={() => {}}
						/>
					</Timeline.Item>
					<Timeline.Item>
						<SectionHeader
							label={checkInHeader}
							expandable
							expanded={expanded.checkIn}
							onToggle={() => setExpanded((prev) => ({ ...prev, checkIn: !prev.checkIn }))}
						/>
						<div style={{ display: expanded.checkIn ? 'block' : 'none' }}>{checkInForm}</div>
					</Timeline.Item>
					<Timeline.Item>
						<SectionHeader
							label={checkOutHeader}
							expandable
							expanded={expanded.checkOut}
							onToggle={() => setExpanded((prev) => ({ ...prev, checkOut: !prev.checkOut }))}
						/>
						<div style={{ display: expanded.checkOut ? 'block' : 'none' }}>{checkOutForm}</div>
					</Timeline.Item>
					{serviceCall.checkInTime && serviceCall.checkOutTime ? (
						<Timeline.Item>
							<div style={{ fontSize: 16 }}>
								On site for{' '}
								{getTimeDuration(
									clampCheckOutToCheckIn(serviceCall.checkInTime, serviceCall.checkOutTime),
									serviceCall.checkInTime,
									{
										hideSeconds: true,
									}
								) || 'less than a minute'}
							</div>
						</Timeline.Item>
					) : null}
				</Timeline>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config,
	updating: state.service_calls.updating,
	resolutionTypes: state.resolution_types,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateServiceCall: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? serviceCallsRestCrudThunksForSupplier.update(entity)
				: serviceCallsRestCrudThunksForBuyer.update(entity)
		),
	fetchMultipleResolutionTypes: (ids, targetCollectionName) =>
		dispatch(resolutionTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchResolutionTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			resolutionTypesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Form.create<ReviseServiceCallModalProps>()(ReviseServiceCallModalInner));
